import { Injectable } from '@angular/core';
import { MapCoordinate } from '../model/quake';

export interface SunEclipticPosition {
  R: number;
  lambda: number;
}
export interface SunEquatorialPosition {
  alpha: number;
  delta: number;
}


@Injectable({
  providedIn: 'root'
})
export class TerminatorService {
  resolution =  2;
  version = '1.1.1';
  _R2D = 180 / Math.PI;
  _D2R = Math.PI / 180;

  /* Terminator.js -- Overlay day/night region on a Leaflet map */

  getJulian(date: Date = new Date()) {
    /* Calculate the present UTC Julian Date. Function is valid after
     * the beginning of the UNIX epoch 1970-01-01 and ignores leap
     * seconds. */
    return (date.getTime() / 86400000) + 2440587.5;
  }

  getGMST(date: Date): number {
    /* Calculate Greenwich Mean Sidereal Time according to
       http://aa.usno.navy.mil/faq/docs/GAST.php */
    const julianDay = this.getJulian(date);
    const d = julianDay - 2451545.0;
    // Low precision equation is good enough for our purposes.
    return (18.697374558 + 24.06570982441908 * d) % 24;
  }
  sunEclipticPosition(julianDay: number): SunEclipticPosition {
    /* Compute the position of the Sun in ecliptic coordinates at
       julianDay.  Following
       http://en.wikipedia.org/wiki/Position_of_the_Sun */
    // Days since start of J2000.0
    const n = julianDay - 2451545.0;
    // mean longitude of the Sun
    let L = 280.460 + 0.9856474 * n;
    L %= 360;
    // mean anomaly of the Sun
    let g = 357.528 + 0.9856003 * n;
    g %= 360;
    // ecliptic longitude of Sun
    const lambda = L + 1.915 * Math.sin(g * this._D2R) +
      0.02 * Math.sin(2 * g * this._D2R);
    // distance from Sun in AU
    const R = 1.00014 - 0.01671 * Math.cos(g * this._D2R) -
      0.0014 * Math.cos(2 * g * this._D2R);
    return { lambda, R };
  }
  eclipticObliquity(julianDay: number): number {
    // Following the short term expression in
    // http://en.wikipedia.org/wiki/Axial_tilt#Obliquity_of_the_ecliptic_.28Earth.27s_axial_tilt.29
    const n = julianDay - 2451545.0;
    // Julian centuries since J2000.0
    const T = n / 36525;
    const epsilon = 23.43929111 -
      T * (46.836769 / 3600
        - T * (0.0001831 / 3600
          + T * (0.00200340 / 3600
            - T * (0.576e-6 / 3600
              - T * 4.34e-8 / 3600))));
    return epsilon;
  }

  sunEquatorialPosition(sunEclLng: number, eclObliq: number): SunEquatorialPosition {
    /* Compute the Sun's equatorial position from its ecliptic
     * position. Inputs are expected in degrees. Outputs are in
     * degrees as well. */
    let alpha = Math.atan(Math.cos(eclObliq * this._D2R)
      * Math.tan(sunEclLng * this._D2R)) * this._R2D;
    const delta = Math.asin(Math.sin(eclObliq * this._D2R)
      * Math.sin(sunEclLng * this._D2R)) * this._R2D;

    const lQuadrant = Math.floor(sunEclLng / 90) * 90;
    const raQuadrant = Math.floor(alpha / 90) * 90;
    alpha = alpha + (lQuadrant - raQuadrant);

    return { alpha, delta };
  }
  hourAngle(lng, sunPos, gst) {
    /* Compute the hour angle of the sun for a longitude on
     * Earth. Return the hour angle in degrees. */
    const lst = gst + lng / 15;
    return lst * 15 - sunPos.alpha;
  }
  latitude(ha: number, sunPos: SunEquatorialPosition): number {
    /* For a given hour angle and sun position, compute the
     * latitude of the terminator in degrees. */
    const lat = Math.atan(-Math.cos(ha * this._D2R) /
      Math.tan(sunPos.delta * this._D2R)) * this._R2D;
    return lat;
  }

  compute(time: Date = new Date()): MapCoordinate[] {
    const julianDay = this.getJulian(time);
    const gst = this.getGMST(time);
    const latLng: MapCoordinate[] = [];

    const sunEclPos = this.sunEclipticPosition(julianDay);
    const eclObliq = this.eclipticObliquity(julianDay);

    const sunEqPos = this.sunEquatorialPosition(sunEclPos.lambda, eclObliq);
    for (let i = 0; i <= 720 * this.resolution; i++) {
      const lng = -360 + i / this.resolution;
      const ha = this.hourAngle(lng, sunEqPos, gst);
      const lat = this.latitude(ha, sunEqPos);
      latLng[i + 1] = new MapCoordinate(lat, lng);
    }
    if (sunEqPos.delta < 0) {
      latLng[0] = new MapCoordinate(90, -180);
      latLng[latLng.length] = new MapCoordinate(90, 180);
      latLng.push(latLng[0]);
    } else {
      latLng[0] = new MapCoordinate(-90, -180);
      latLng[latLng.length] = new MapCoordinate(-90, 180);
      latLng.push(latLng[0]);

    }
    return latLng;
  }
}
